/**
 * Route Configuration
 *
 * Centralized configuration for route protection and layout management.
 * This ensures consistency across the application and makes it easy to maintain.
 */

export interface RouteConfig {
  /** Routes that should never redirect to login (OAuth flows, callbacks, etc.) */
  noAuthRedirect: string[]

  /** Auth-related routes (login, signup, etc.) */
  auth: string[]

  /** Provider dashboard routes */
  provider: string[]

  /** Operation dashboard routes */
  operation: string[]

  /** Customer portal routes */
  customer: string[]

  /** Shared routes (password reset links, etc.) */
  shared: string[]
}

export const ROUTE_CONFIG: RouteConfig = {
  // Routes that should NEVER redirect to login
  // These are OAuth flows, callbacks, and other special pages
  noAuthRedirect: [
    '/auth/login', // OAuth customer login page
    '/auth/callback', // OAuth callbacks (all variants)
    '/customer/checkout', // Checkout pages
    '/customer/manage', // Customer management pages
    '/operation/login', // Super admin login page
    '/auth/mcp-connect', // MCP provider OAuth consent page (legacy)
    '/auth/provider-mcp/', // Provider MCP OAuth (IdP + consent)
    '/auth/customer/consent', // Customer OAuth consent page
  ],

  auth: ['/login', '/sign-up', '/verify-email', '/accept-invitation', '/success'],

  // Provider dashboard routes
  provider: ['/provider/'],

  // Operation dashboard routes
  operation: ['/operation/'],

  // Customer portal routes
  customer: ['/customer/'],

  shared: ['/shared/'],
}

/**
 * Check if a route matches any pattern in the given list
 */
export function matchesRoute(pathname: string, routes: string[]): boolean {
  return routes.some(route => {
    // Special case for root path to avoid matching everything
    if (route === '/') {
      return pathname === '/'
    }

    if (route.endsWith('/')) {
      // Prefix match for routes ending with /
      return pathname.startsWith(route)
    }
    // Exact match for routes without trailing slash
    return pathname === route
  })
}

/**
 * Check if a route should never redirect to login
 */
export function isNoAuthRedirectRoute(pathname: string): boolean {
  return matchesRoute(pathname, ROUTE_CONFIG.noAuthRedirect)
}

/**
 * Check if a route is an auth route
 */
export function isAuthRoute(pathname: string): boolean {
  return matchesRoute(pathname, ROUTE_CONFIG.auth)
}

/**
 * Check if a route is a provider route
 */
export function isProviderRoute(pathname: string): boolean {
  return (
    matchesRoute(pathname, ROUTE_CONFIG.provider) &&
    pathname !== '/provider/register' &&
    !pathname.startsWith('/provider/onboarding/')
  )
}

/**
 * Check if a route is a customer route
 */
export function isCustomerRoute(pathname: string): boolean {
  // Exclude checkout and manage pages as they have special handling
  return (
    matchesRoute(pathname, ROUTE_CONFIG.customer) &&
    !pathname.startsWith('/customer/checkout') &&
    !pathname.startsWith('/customer/manage')
  )
}

/**
 * Check if a route is a shared route
 */
export function isSuperAdminRoute(pathname: string): boolean {
  return matchesRoute(pathname, ROUTE_CONFIG.operation) && pathname !== '/operation/login'
}

export function isSharedRoute(pathname: string): boolean {
  return matchesRoute(pathname, ROUTE_CONFIG.shared)
}
